import type { Employment, MonthYear } from "@components/quick_apply/employments";
import { Education } from "@components/quick_apply/education_section.tsx";
import { FileState } from "@/queries/use_profile.ts";

export interface EmploymentError {
  company?: string;
  title?: string;
  startDateMonth?: string;
  startDateYear?: string;
  endDateMonth?: string;
  endDateYear?: string;
  endDateYearInvalid?: string;
}

export interface EmploymentErrors {
  [index: number]: EmploymentError;
}

function isEmptyMonthYear(monthYear?: MonthYear | null): boolean {
  if (!monthYear) {
    return true;
  }

  return !monthYear.month && !monthYear.year;
}

function isEmptyEmployment(employment: Partial<Employment>): boolean {
  return (
    !employment.company &&
    !employment.title &&
    isEmptyMonthYear(employment.startDate) &&
    isEmptyMonthYear(employment.endDate)
  );
}

export function endDateAfterStartDate(
  startMonth?: number | null,
  startYear?: number | null,
  endMonth?: number | null,
  endYear?: number | null,
): boolean {
  if (startYear != null && endYear != null) {
    if (startYear > endYear) {
      return false;
    } else if (startYear === endYear) {
      if (startMonth == null || endMonth == null) {
        return true;
      } else if (startMonth >= endMonth) {
        return false;
      }
    }
  }

  return true;
}

export function validateEmployments(employments: Partial<Employment>[]): EmploymentErrors {
  const errors: EmploymentErrors = {};

  employments.forEach((employment, index) => {
    const error = {} as EmploymentError;

    if (isEmptyEmployment(employment)) {
      return;
    }

    if (!employment.company?.trim()) {
      error.company = "employment.companyRequired";
    }

    if (!employment.title?.trim()) {
      error.title = "employment.titleRequired";
    }

    if (!employment.startDate?.month) {
      error.startDateMonth = "dates.startDateMonthRequired";
    }

    if (!employment.startDate?.year) {
      error.startDateYear = "dates.startDateYearRequired";
    }

    // We only need to validate end date if either field is provided.
    // Otherwise we can consider this a current employment.
    if (!isEmptyMonthYear(employment.endDate)) {
      if (!employment.endDate?.month) {
        error.endDateMonth = "dates.endDateMonthRequired";
      } else if (!employment.endDate?.year) {
        error.endDateYear = "dates.endDateYearRequired";
      }
    }

    if (
      !endDateAfterStartDate(
        employment.startDate?.month,
        employment.startDate?.year,
        employment.endDate?.month,
        employment.endDate?.year,
      )
    ) {
      error.endDateYearInvalid = "dates.dateRangeInvalid";
    }

    if (Object.keys(error).length > 0) {
      errors[index] = error;
    }
  });

  return errors;
}

export const ALLOWED_FILETYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/rtf",
  "application/rtf",
];

export const MAX_FILE_SIZE_MB = 10;

const megabytesToBytes = (mb: number) => mb * 1024 * 1024;

export const validateResume = (resume: FileState | null): undefined | string => {
  if (!resume?.file) {
    return;
  }

  if (!ALLOWED_FILETYPES.includes(resume.file.type)) {
    return "resume.invalidFileType";
  }

  if (resume.file.size > megabytesToBytes(MAX_FILE_SIZE_MB)) {
    return "resume.maxFileSize";
  }
};

export const validateEducations = (
  educations: Array<Education>,
): Array<string | TemplateStringsArray> => {
  const errors = [] as Array<string | TemplateStringsArray>;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  educations.forEach((education: Education, index: number) => {
    // Validate start date is in the past
    if (education.startYear !== undefined) {
      if (education.startYear > currentYear) {
        errors[index] = "dates.startDateError";
        return;
      } else if (education.startYear === currentYear) {
        if (education.startMonth !== undefined && education.startMonth > currentMonth) {
          errors[index] = "dates.startDateError";
          return;
        }
      }
    }

    if (
      !endDateAfterStartDate(
        education.startMonth,
        education.startYear,
        education.endMonth,
        education.endYear,
      )
    ) {
      errors[index] = "dates.dateRangeInvalid";
      return;
    }
  });

  return errors;
};
